import {
  DialogChat,
  DialogReplyPreview,
  TypeDialogMessage,
} from "../../../types/DialogChat";
import strings from "../../../strings";
import { getTranslation } from "../../../utils/getTranslation";
import { GlobalChatMessage } from "./globalChatCache";

export type GlobalChatMentionCandidate = {
  id: string;
  label: string;
};

export const getGlobalChatMentionCandidates = (
  chat: DialogChat | null,
  currentUserId?: string,
  language?: string,
): GlobalChatMentionCandidate[] =>
  (chat?.members || [])
    .filter((member) => member._id !== currentUserId)
    .map((member) => ({
      id: member._id,
      label: `${getTranslation(member.name, language)} ${getTranslation(
        member.surname,
        language,
      )}`.trim(),
    }))
    .filter((member) => member.label.length > 0);

export const dedupeMentionIdsForChat = (
  mentionIds: string[],
  chat: DialogChat | null,
) => {
  const memberIds = new Set((chat?.members || []).map((member) => member._id));

  return Array.from(new Set(mentionIds)).filter((id) => memberIds.has(id));
};

export const insertMentionText = (text: string, mentionLabel: string) => {
  const markerIndex = text.lastIndexOf("@");
  const suffix = `@${mentionLabel} `;

  if (markerIndex < 0) {
    return `${text}${text.endsWith(" ") || text.length === 0 ? "" : " "}${suffix}`;
  }

  return `${text.slice(0, markerIndex)}${suffix}`;
};

export const stripMentionLabels = (
  text: string,
  selectedMentions: GlobalChatMentionCandidate[],
) =>
  selectedMentions
    .reduce(
      (value, mention) =>
        value.replace(new RegExp(`@${escapeRegExp(mention.label)}`, "g"), ""),
      text,
    )
    .trim();

export const isMentionOnlyText = (
  text: string,
  selectedMentions: GlobalChatMentionCandidate[],
) => text.trim().length > 0 && stripMentionLabels(text, selectedMentions) === "";

export const canSendStructuredTextMessage = ({
  text,
  filesCount,
  selectedMentions,
}: {
  text: string;
  filesCount: number;
  selectedMentions: GlobalChatMentionCandidate[];
}) => {
  if (filesCount > 0) {
    return true;
  }

  if (text.trim().length === 0) {
    return false;
  }

  return !isMentionOnlyText(text, selectedMentions);
};

export const canReplyToGlobalChatMessage = (message?: GlobalChatMessage | null) =>
  Boolean(
    message &&
      !message.isLocalOnly &&
      message.user !== "System" &&
      !message.realTimeBooking &&
      !message.deletedAt &&
      message.deliveryState !== "pending" &&
      message.deliveryState !== "failed",
  );

export const createReplyPreviewFromMessage = (
  message: GlobalChatMessage,
): DialogReplyPreview => ({
  _id: message.messageId,
  user: message.user || "System",
  message: message.voice
    ? ""
    : message.text || (message.files.length > 0 ? "" : undefined),
  files: message.files,
  labels: undefined,
  date: new Date(message.date).toISOString(),
});

export const createReplyPreviewFromDialogMessage = (
  message?: TypeDialogMessage | string,
  selectedLanguage?: string,
): DialogReplyPreview | undefined => {
  if (!message || typeof message === "string") {
    return undefined;
  }

  const label = message.labels?.find(
    (item) => item.language === selectedLanguage,
  );

  return {
    _id: message._id,
    user: message.user,
    message: message.voice
      ? ""
      : label?.text ?? message.message ?? (message.files?.length ? "" : undefined),
    files: message.files,
    labels: message.labels,
    date: new Date(message.date).toISOString(),
  };
};

export const getReplyPreviewSummary = (preview?: DialogReplyPreview) => {
  if (!preview) {
    return "";
  }

  if (preview.message?.trim()) {
    return preview.message;
  }

  if (preview.files?.length) {
    return strings.globalChatAttachmentPreview;
  }

  return strings.globalChatVoiceMessagePreview;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
